import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkEmail, snsSignUp } from '../../Module/Auth/Presentation/authActions';
import { useAppDispatch } from '../../store/hooks';
import { PATHS } from '../../routes/paths';
import { SNS_TICKET_INVALID_CODE, getSnsErrorCode, type SnsSignupState } from '../../Utils/snsLogin';

export type FundraiserType = 'individual' | 'group' | 'foreigner';
type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'duplicate' | 'error';
const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';
/** 회원가입 환영 화면에서 '나중에 하기'로 메인 프로젝트로 갈 때 안내 모달을 잠시 띄우지 않음 */
const SIGNUP_SKIP_ACCOUNT_FIRST_KEY = 'missionfund.signupSkippedAccountFirst';

export const useSignup = () => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // 회원가입은 SNS(카카오) 인증을 마친 뒤에만 진행한다. 로그인 화면이 인증 정보를 넘겨 준다
  const snsSignup = (location.state as { snsSignup?: SnsSignupState } | null | undefined)?.snsSignup;
  const snsName = snsSignup?.profile?.name?.trim() ?? '';
  const snsPhone = snsSignup?.profile?.phone?.trim() ?? '';

  const [step, setStep] = useState(0); // 0: Type Selection, 1: Email, 3: Additional Info, 4: Welcome (2: Password 는 SNS 가입에서 쓰지 않음)
  const [fundraiserType, setFundraiserType] = useState<FundraiserType>('individual');
  const [email, setEmail] = useState(() => snsSignup?.profile?.email?.trim() ?? '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [inputName, setInputName] = useState('');
  const [inputPhone, setInputPhone] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>('idle');
  const [emailCheckMessage, setEmailCheckMessage] = useState<string | undefined>(undefined);
  const latestEmailCheckId = useRef(0);
  const isSignedUpRef = useRef(false);

  // SNS 인증 정보 없이 들어온 경우(주소 직접 입력 등) 로그인 화면으로 보낸다
  useEffect(() => {
    if (!snsSignup?.ticket && !isSignedUpRef.current) {
      navigate(PATHS.LOGIN, { replace: true });
    }
  }, [navigate, snsSignup]);

  const isIndividual = fundraiserType === 'individual';
  // 개인 회원의 이름과 전화번호는 SNS 인증 값으로 고정한다 (SNS 값이 비어 있을 때만 입력)
  // 단체 회원의 이름 칸은 단체명이므로 직접 입력한다
  const isNameLocked = isIndividual && !!snsName;
  const isPhoneLocked = !!snsPhone;
  const name = isNameLocked ? snsName : inputName;
  const phoneNumber = isPhoneLocked ? snsPhone : inputPhone;

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isEmailFormatValid = useCallback((value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), []);
  const isEmailAvailable = emailCheckStatus === 'available';

  useEffect(() => {
    if (!trimmedEmail) {
      setEmailCheckStatus('idle');
      setEmailCheckMessage(undefined);
      return;
    }

    if (!isEmailFormatValid(trimmedEmail)) {
      setEmailCheckStatus('error');
      setEmailCheckMessage(t('page.signup.validation.email.invalid'));
      return;
    }

    const currentCheckId = ++latestEmailCheckId.current;
    const timer = window.setTimeout(async () => {
      setEmailCheckStatus('checking');
      setEmailCheckMessage(t('page.signup.validation.email.checking'));

      try {
        const result = await dispatch(checkEmail({ email: trimmedEmail })).unwrap();
        if (latestEmailCheckId.current !== currentCheckId) return;

        if (result.available) {
          setEmailCheckStatus('available');
          setEmailCheckMessage(t('page.signup.validation.email.available'));
          return;
        }

        setEmailCheckStatus('duplicate');
        setEmailCheckMessage(t('page.signup.validation.email.duplicate'));
      } catch (e) {
        if (latestEmailCheckId.current !== currentCheckId) return;
        const msg = typeof e === 'string' ? e : (e as Error)?.message;
        setEmailCheckStatus('error');
        setEmailCheckMessage(msg || t('page.signup.validation.email.error'));
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [dispatch, isEmailFormatValid, t, trimmedEmail]);

  const passwordMessage = useMemo(() => {
    if (password && passwordConfirm && password !== passwordConfirm) {
      return t('page.signup.validation.passwordMismatch');
    }
    return undefined;
  }, [password, passwordConfirm, t]);

  const handleNext = useCallback(() => {
    setStep((prev) => {
      if (prev === 1 && !isEmailAvailable) return prev;
      // SNS 가입은 비밀번호를 입력받지 않는다 (1 → 3)
      if (prev === 1) return 3;
      return prev + 1;
    });
  }, [isEmailAvailable]);

  const handlePrev = useCallback(() => {
    setStep((prev) => {
      if (prev <= 0) {
        navigate(-1);
        return 0;
      }
      if (prev === 3) return 1;
      return prev - 1;
    });
  }, [navigate]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    if (!snsSignup?.ticket) return;
    if (!isEmailAvailable) {
      setError(emailCheckMessage || t('page.signup.errors.default'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(undefined);

      // 로그인 직후 안내 모달이 환영 화면·나의 계정 화면을 가리지 않게 미리 표시해 둔다
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SUPPRESS_POST_SIGNUP_MODALS_KEY, '1');
      }
      isSignedUpRef.current = true;

      // 개인 회원: 이름은 서버가 SNS 인증 값으로 first_name 에 저장 (SNS 값이 없을 때만 name 사용)
      // 단체 회원: 입력한 단체명을 organization 에 저장
      // 가입과 동시에 로그인된다 (비밀번호 없음 — SNS 로 로그인)
      await dispatch(
        snsSignUp({
          ticket: snsSignup.ticket,
          email: email.trim(),
          // 회원 유형은 상세 코드(Missionary/NGO 등)로 저장
          user_type: category,
          user_desc: category,
          ...(isIndividual ? { name: inputName } : { organization: inputName.trim() }),
          phone: inputPhone,
        }),
      ).unwrap();

      setStep(4);
    } catch (e) {
      isSignedUpRef.current = false;
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
      }
      const msg = typeof e === 'string' ? e : (e as Error)?.message;
      const code = getSnsErrorCode(msg);
      if (code === SNS_TICKET_INVALID_CODE) {
        // 인증 유효 시간이 지남 — SNS 인증부터 다시
        window.alert(t('page.login.sns.errors.ticketExpired'));
        navigate(PATHS.LOGIN, { replace: true });
        return;
      }
      if (code === 40050 && msg?.includes('Duplicated Email')) {
        setError(t('page.signup.validation.email.duplicate'));
      } else {
        setError(t('page.signup.errors.default'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [category, dispatch, email, emailCheckMessage, inputName, inputPhone, isEmailAvailable, isIndividual, isSubmitting, navigate, snsSignup, t]);

  const goToLogin = useCallback(() => {
    const userId = window.localStorage.getItem('missionfund.userId');
    navigate(userId ? PATHS.DASHBOARD : PATHS.LOGIN);
  }, [navigate]);

  const goToAccount = useCallback(() => {
    navigate(PATHS.MY_ACCOUNT, { replace: true });
  }, [navigate]);

  const goToDashboard = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIGNUP_SKIP_ACCOUNT_FIRST_KEY, '1');
    }
    navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
  }, [navigate]);

  return {
    step,
    setStep,
    fundraiserType,
    setFundraiserType,
    email,
    setEmail,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    name,
    setName: setInputName,
    isNameLocked,
    phoneNumber,
    setPhoneNumber: setInputPhone,
    isPhoneLocked,
    category,
    setCategory,
    handleNext,
    handlePrev,
    handleConfirm,
    isSubmitting,
    error,
    emailCheckStatus,
    emailCheckMessage,
    isEmailAvailable,
    passwordMessage,
    goToLogin,
    goToAccount,
    goToDashboard,
  };
};
